import { api } from "./apiClient";

/**
 * Web Push on this device (request 1069). The service worker (/sw.js) shows pushes as phone / desktop notifications even
 * when Onerra is closed. iPhone / iPad only allow it once Onerra is added to the Home Screen (iOS 16.4+).
 */
export const PUSH_FLAG = "onerra-push-on";

export const pushSupported = () => typeof window !== "undefined" && "serviceWorker" in navigator && "PushManager" in window && "Notification" in window;
export const isIos = () => /iphone|ipad|ipod/i.test(navigator.userAgent) || (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1);
export const isStandalone = () => window.matchMedia?.("(display-mode: standalone)").matches || (navigator as any).standalone === true;

/** Registers the service worker once (from main.tsx). */
export function registerServiceWorker() {
  if (!("serviceWorker" in navigator)) return;
  window.addEventListener("load", () => {
    navigator.serviceWorker.register("/sw.js").catch(() => undefined);
  });
}

function urlBase64ToUint8Array(base64: string) {
  const padding = "=".repeat((4 - (base64.length % 4)) % 4);
  const raw = atob((base64 + padding).replace(/-/g, "+").replace(/_/g, "/"));
  return Uint8Array.from([...raw].map((c) => c.charCodeAt(0)));
}

async function registration() {
  const reg = (await navigator.serviceWorker.getRegistration()) ?? (await navigator.serviceWorker.register("/sw.js"));
  await navigator.serviceWorker.ready;
  return reg;
}

export type PushState = "unsupported" | "needs-install" | "denied" | "off" | "on";

/** What this device can do right now. */
export async function pushState(): Promise<PushState> {
  if (!pushSupported()) return isIos() && !isStandalone() ? "needs-install" : "unsupported";
  if (Notification.permission === "denied") return "denied";
  const reg = await navigator.serviceWorker.getRegistration();
  const sub = await reg?.pushManager.getSubscription();
  const on = !!sub && Notification.permission === "granted";
  try {
    on ? localStorage.setItem(PUSH_FLAG, "1") : localStorage.removeItem(PUSH_FLAG);
  } catch {
    /* storage blocked — nothing to remember */
  }
  return on ? "on" : "off";
}

/** Asks permission, subscribes this browser and registers it with the server. */
export async function enablePush(): Promise<PushState> {
  if (!pushSupported()) return pushState();
  const perm = await Notification.requestPermission();
  if (perm !== "granted") return perm === "denied" ? "denied" : "off";
  const reg = await registration();
  const { publicKey } = (await api.get<{ data: { publicKey: string } }>("/notifications/push/public-key")).data.data;
  let sub = await reg.pushManager.getSubscription();
  if (!sub) sub = await reg.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: urlBase64ToUint8Array(publicKey) });
  const json = sub.toJSON() as { endpoint: string; keys: { p256dh: string; auth: string } };
  await api.post("/notifications/push/subscribe", { endpoint: json.endpoint, keys: json.keys });
  return pushState();
}

export async function disablePush(): Promise<PushState> {
  const reg = await navigator.serviceWorker.getRegistration();
  const sub = await reg?.pushManager.getSubscription();
  if (sub) {
    await api.post("/notifications/push/unsubscribe", { endpoint: sub.endpoint }).catch(() => undefined);
    await sub.unsubscribe().catch(() => undefined);
  }
  return pushState();
}

export async function sendTestPush(): Promise<number> {
  return (await api.post<{ data: { sent: number } }>("/notifications/push/test")).data.data.sent;
}

/**
 * On every sign-in: if this browser already allows Onerra's notifications, make sure it's subscribed and registered to
 * the signed-in person (so people who allowed notifications get push without visiting Settings).
 */
export async function resyncPush() {
  try {
    if (!pushSupported() || Notification.permission !== "granted") return;
    const reg = await registration();
    let sub = await reg.pushManager.getSubscription();
    if (!sub) {
      const { publicKey } = (await api.get<{ data: { publicKey: string } }>("/notifications/push/public-key")).data.data;
      sub = await reg.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: urlBase64ToUint8Array(publicKey) });
    }
    const json = sub.toJSON() as { endpoint: string; keys: { p256dh: string; auth: string } };
    await api.post("/notifications/push/subscribe", { endpoint: json.endpoint, keys: json.keys });
    try {
      localStorage.setItem(PUSH_FLAG, "1");
    } catch {
      /* storage blocked */
    }
  } catch {
    /* not signed in yet, or offline — try again next time */
  }
}
